import type { Empresa, PadronSummary } from '@lib/types';

/**
 * Cómo mostrar el plan y el promotor con los que se cargó un padrón.
 *
 * Los padrones anteriores a la feature (los 136 de prod) no tienen los campos:
 * entraron con el plan base y el promotor de la organización, y así se dicen,
 * en vez de un "—" que se leería como dato perdido.
 *
 * El plan se identifica por código + sucursal: el mismo plan puede estar en
 * varias sucursales del empleador, y la etiqueta la muestra cuando el padrón
 * la tiene congelada.
 */
export function planDeCarga(
  p: Pick<PadronSummary, 'planCodigo' | 'sucursalId' | 'empresaId'>,
  empresas: Empresa[] | undefined,
): { codigo: string | null; etiqueta: string } {
  const empresa = (empresas ?? []).find((e) => e.id === p.empresaId);
  const planes = empresa?.altaConfig?.planes ?? [];
  if (p.planCodigo) {
    const delCodigo = planes.filter((pl) => pl.codigo === p.planCodigo);
    // Con sucursal congelada se busca ESA fila; sin ella, la base del código o
    // la primera (es lo mismo que resuelve el server para un padrón viejo).
    const plan = p.sucursalId
      ? delCodigo.find((pl) => (pl.sucursalId ?? '') === p.sucursalId)
      : (delCodigo.find((pl) => pl.esBase) ?? delCodigo[0]);
    const nombre = plan?.nombre ? `${plan.nombre} (${p.planCodigo})` : p.planCodigo;
    return {
      codigo: p.planCodigo,
      etiqueta: p.sucursalId ? `${nombre} · suc ${p.sucursalId}` : nombre,
    };
  }
  const base = planes.find((pl) => pl.esBase) ?? (planes.length === 1 ? planes[0] : undefined);
  const codigoBase = base?.codigo ?? empresa?.planBaseCodigo ?? null;
  if (!codigoBase) return { codigo: null, etiqueta: 'Plan base de la organización' };
  return {
    codigo: codigoBase,
    etiqueta: `${base?.nombre ? `${base.nombre} (${codigoBase})` : codigoBase} · base de la organización`,
  };
}

export function promotorDeCarga(
  p: Pick<PadronSummary, 'promotorUsername' | 'empresaId'>,
  empresas: Empresa[] | undefined,
): string {
  if (p.promotorUsername) return p.promotorUsername;
  const empresa = (empresas ?? []).find((e) => e.id === p.empresaId);
  const delaEmpresa = empresa?.altaConfig?.promotorId;
  return delaEmpresa ? `${delaEmpresa} · de la organización` : '—';
}
